import { Cell } from 'react-table';
import Image from 'next/image';
import ProgressBar from '@ramonak/react-progress-bar';

import { theme } from '../../tailwind.config';
import {
  BodyExtraSmallRegular,
  BodyRegular,
  BodyRegularBold,
  BodySmallRegular,
  BodySmallRegularBold,
  CaptionBold,
  CaptionRegular
} from '@components/atoms/typography';
import Icon from '@components/atoms/icon';
import { BLANK_SHIRT_PATH } from '@lib/utils';
import { useTeamKitImages } from '@hooks/useTeamKitImages';
import PlayerFaceWithShirtFallback from '@components/player/face-with-shirt-fallback';
import { LeagueEntry } from '@api-service/mini-leagues';
import { MiniLeagueRowCaptain } from '@components/Table/table-transformers';
import Tooltip from '@components/atoms/tooltip';
import type { Player } from '@api-service/players';

const POSITION_SHORT_LABELS: Record<Player['position'], string> = {
  GK: 'GK',
  DEF: 'DEF',
  MID: 'MID',
  FWD: 'FWD'
};

const CHANGE_TIME_SORT_VALUES: Record<string, number> = {
  Tonight: 1,
  Tomorrow: 2,
  '2 Days': 3,
  '1 Week': 4,
  '>1 Week': 5,
  Locked: 6
};

const changeTimeSortValue = (text: string): number =>
  CHANGE_TIME_SORT_VALUES[text] ?? CHANGE_TIME_SORT_VALUES.Locked;

const PriceRisePlayerCell = ({
  player,
  isMobile
}: {
  player: Player;
  isMobile: boolean;
}) => (
  <div
    data-cy={`price-rise-player-position-${player.position}`}
    className={`z-50 bg-white ${isMobile ? 'max-w-[100px]' : ''}`}
  >
    <div className="flex items-center mb-1">
      <div className="h-8 w-8 mr-2 flex items-center justify-center">
        <PlayerFaceWithShirtFallback player={player} />
      </div>
      <BodyRegularBold
        className={`mb-0 ${isMobile ? 'truncate ... w-[80px]' : ''}`}
        data-cy={`price-rise-player-${player.displayName}`}
      >
        {player.displayName}
      </BodyRegularBold>
    </div>
    <div className="flex items-center">
      <CaptionBold textColor="text-neutral-600">
        {player.team.shortName}
      </CaptionBold>
      <div className="w-1 h-1 bg-neutral-400 mx-1 rounded" />
      <CaptionRegular>{POSITION_SHORT_LABELS[player.position]}</CaptionRegular>
    </div>
  </div>
);

const TeamShortNameShirt = ({ player }: { player: MiniLeagueRowCaptain }) => {
  // Hooks run before the early return so their order never varies.
  const { resolveTeamKit } = useTeamKitImages();
  if (!player) return null;

  // The backend has a real goalkeeper kit per team; the bundled set had one
  // generic GK shirt for all twenty.
  const remoteKit = resolveTeamKit(
    player.teamShortName,
    player.position === 'GK'
  );

  return (
    <Image
      src={remoteKit ?? BLANK_SHIRT_PATH}
      alt={`${player.teamShortName} shirt`}
      width={20}
      height={20}
      unoptimized
    />
  );
};

export enum PriceRiseColumnAccessors {
  Player = 'player',
  Price = 'price',
  Progress = 'progress',
  Direction = 'direction',
  ChangeTime = 'changeTime',
  HourlyRate = 'hourlyRate'
}

const compareNumeric = (rowA, rowB, id, desc) => {
  // NaN values are clamped to the same +/-Infinity so the comparator
  // stays commutative (compare(a,b) === -compare(b,a)). Returning a fixed
  // sign on the first NaN check would break that contract and produce
  // unstable orderings in react-table.
  const nanReplacement = desc
    ? Number.NEGATIVE_INFINITY
    : Number.POSITIVE_INFINITY;
  const rawA = Number(rowA.values[id]);
  const rawB = Number(rowB.values[id]);
  const a = Number.isNaN(rawA) ? nanReplacement : rawA;
  const b = Number.isNaN(rawB) ? nanReplacement : rawB;
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
};

const compareChangeTime = (rowA, rowB, id) => {
  const a = rowA.values[id]?.sortValue ?? 0;
  const b = rowB.values[id]?.sortValue ?? 0;
  const secondaryA = Number(rowA.values.progress);
  const secondaryB = Number(rowB.values.progress);
  if (a === b) {
    if (secondaryA < 0 && secondaryB < 0) {
      return secondaryA > secondaryB ? 1 : -1;
    }
    return secondaryA < secondaryB ? 1 : -1;
  }
  return a < b ? -1 : 1;
};

export type PriceRiseRow = {
  player: Player;
  price: string;
  progress: number;
  direction: number;
  changeTime: { text: string; sortValue: number };
  hourlyRate: number;
};

export const toPriceRiseRow = (player: Player): PriceRiseRow => {
  const progress = player.priceChange?.progress ?? 0;
  const changeTimeText = player.priceChange?.estimatedChangeTime ?? 'Locked';
  return {
    player,
    price: `£${player.price.toFixed(1)}m`,
    progress,
    direction: progress === 0 ? 0 : progress > 0 ? 1 : -1,
    changeTime: {
      text: changeTimeText,
      sortValue: changeTimeSortValue(changeTimeText)
    },
    hourlyRate: player.priceChange?.hourlyRate ?? 0
  };
};

export const generatePriceRiseColumns = ({
  isMobile
}: {
  isMobile: boolean;
}) => {
  const changeTimeColumn = {
    Header: () => (
      <div className="flex items-center">
        <BodySmallRegularBold textColor="text-neutral-600">
          Change time
        </BodySmallRegularBold>
        <div className="hidden sm:flex">
          <Tooltip
            variant="light"
            title="This estimates when we believe the price will change."
          >
            <span className="ml-1">
              <Icon className="w-4 h-4" name="InfoFilled" />
            </span>
          </Tooltip>
        </div>
      </div>
    ),
    accessor: PriceRiseColumnAccessors.ChangeTime,
    disableFilters: true,
    sortType: compareChangeTime,
    minWidth: 100,
    Cell: (cell: Cell) => (
      <div className="flex items-center min-w-[100px]">
        {cell.value?.text === 'Tonight' && (
          <Icon className="text-warning-600 mr-2" name="Warning" />
        )}
        <BodyRegular
          textColor={
            cell.value?.text === 'Tonight'
              ? 'text-warning-600'
              : 'text-neutral-800'
          }
        >
          {cell.value?.text}
        </BodyRegular>
      </div>
    )
  };

  return [
    {
      Header: 'Player',
      accessor: PriceRiseColumnAccessors.Player,
      disableSortBy: true,
      disableFilters: true,
      sticky: 'left',
      Cell: ({ value }: Cell) => (
        <PriceRisePlayerCell player={value} isMobile={isMobile} />
      )
    },
    ...(isMobile ? [changeTimeColumn] : []),
    {
      Header: () => (
        <div className="flex items-center">
          <BodySmallRegularBold textColor="text-neutral-600">
            Progress
          </BodySmallRegularBold>
          <div className="hidden sm:flex">
            <Tooltip
              variant="light"
              title="The player's price is predicted to increase once it hits 100% and is predicted to decrease once it hits -100%."
            >
              <span className="ml-1">
                <Icon className="w-4 h-4" name="InfoFilled" />
              </span>
            </Tooltip>
          </div>
        </div>
      ),
      accessor: PriceRiseColumnAccessors.Progress,
      disableFilters: true,
      maxWidth: 150,
      sortType: compareNumeric,
      Cell: (cell: Cell) => {
        const progress = Number(cell.value);
        const absProgress = Math.abs(progress);
        return (
          <div className={`${isMobile ? 'w-[80px]' : 'w-[100px]'}`}>
            <ProgressBar
              bgColor={
                progress < 0
                  ? theme.colors.danger[100]
                  : theme.colors.primary[100]
              }
              borderRadius="0px"
              height="22px"
              completed={absProgress}
              className="mt-2"
              dir={progress < 0 ? 'rtl' : 'ltr'}
              barContainerClassName="text-center bg-neutral-100 relative rounded-lg"
              customLabel={`${absProgress.toFixed(0)}%${
                progress < 0 ? '-' : ''
              }`}
              labelClassName={`text-neutral-800 font-sans font-normal text-xxs absolute ${
                isMobile ? 'top-[4px] left-[25px]' : 'top-[4px] left-[36px]'
              } `}
            />
          </div>
        );
      }
    },
    {
      Header: 'Price',
      accessor: PriceRiseColumnAccessors.Price,
      disableFilters: true,
      disableSortBy: true
    },
    {
      Header: () => (
        <div className="flex items-center">
          <BodySmallRegularBold textColor="text-neutral-600">
            Price prediction
          </BodySmallRegularBold>
          <div className="hidden sm:flex">
            <Tooltip
              variant="light"
              title="A price increase/decrease will change the player's value by £0.1m"
            >
              <span className="ml-1">
                <Icon className="w-4 h-4" name="InfoFilled" />
              </span>
            </Tooltip>
          </div>
        </div>
      ),
      accessor: PriceRiseColumnAccessors.Direction,
      disableFilters: true,
      Cell: (cell: Cell) => {
        const direction = Number(cell.value);
        return (
          <div className="flex items-center">
            {direction !== 0 && (
              <Icon
                className={`w-5 h-5 mr-1 ${
                  direction > 0
                    ? 'text-success-600'
                    : 'text-danger-600 rotate-90'
                }`}
                name="ArrowUpRightFilled"
              />
            )}
            <BodyRegular>
              {direction === 0 ? '-' : direction > 0 ? 'Increase' : 'Decrease'}
            </BodyRegular>
          </div>
        );
      }
    },
    {
      Header: () => (
        <div className="flex items-center">
          <BodySmallRegularBold textColor="text-neutral-600">
            Hourly change
          </BodySmallRegularBold>
          <div className="hidden sm:flex">
            <Tooltip
              variant="light"
              title="This column shows how fast the Progress column is going up or down."
            >
              <span className="ml-1">
                <Icon className="w-4 h-4" name="InfoFilled" />
              </span>
            </Tooltip>
          </div>
        </div>
      ),
      accessor: PriceRiseColumnAccessors.HourlyRate,
      disableFilters: true,
      sortType: compareNumeric,
      Cell: (cell: Cell) => {
        const value = Number(cell.value);
        return (
          <div className="flex items-center w-[100px]">
            <BodyRegular>{value.toFixed(2)}%</BodyRegular>
            <div className="flex items-center ml-2">
              {transfersTrendGenerator(value)}
            </div>
          </div>
        );
      }
    },
    ...(!isMobile ? [changeTimeColumn] : [])
  ];
};

export const transfersTrendGenerator = (value: number) => {
  if (value > 1.5)
    return (
      <div className="flex items-center">
        <div className="mb-0 mr-2 min-w-[16px] h-[15px] flex items-center">
          <img src="/images/three-chevron-up.png" alt="3 up arrow" />
        </div>
      </div>
    );
  if (value > 0.6)
    return (
      <div className="flex items-center">
        <div className="mr-2 min-w-[16px] h-[15px] flex items-center">
          <img
            className="mb-0"
            src="/images/two-chevron-up.png"
            alt="2 up arrow"
          />
        </div>
      </div>
    );
  if (value > 0)
    return (
      <div className="flex items-center">
        {' '}
        <div className="mb-0 mr-2 min-w-[16px] h-[15px] flex items-center">
          <img
            className="mb-0"
            src="/images/one-chevron-up.png"
            alt="1 up arrow"
          />
        </div>
      </div>
    );
  if (value < -1.5)
    return (
      <div className="flex items-center">
        <div className="mb-0 mr-2 min-w-[16px] h-[15px] flex items-center">
          <img
            className="mb-0"
            src="/images/three-chevron-down.png"
            alt="3 down arrow"
          />
        </div>
      </div>
    );
  if (value < -0.6)
    return (
      <div className="flex items-center">
        <div className="mb-0 mr-2 min-w-[16px] h-[15px] flex items-center">
          <img
            className="mb-0"
            src="/images/two-chevron-down.png"
            alt="2 down arrow"
          />
        </div>
      </div>
    );
  if (value === 0) return <div className="flex items-center"></div>;
  return (
    <div className="flex items-center">
      <div className="mb-0 mr-2 min-w-[16px] h-[15px] flex items-center">
        <img
          className="mb-0"
          src="/images/one-chevron-down.png"
          alt="1 down arrow"
        />
      </div>
    </div>
  );
};

export enum MiniLeagueColumnAccessors {
  leaguePosition = 'leaguePosition',
  liveRank = 'liveRank',
  team = 'team',
  total = 'total',
  points = 'points',
  hits = 'hits',
  captain = 'captain',
  viceCaptain = 'viceCaptain',
  transfers = 'transfers',
  played = 'played',
  teamId = 'teamId',
  chipInPlay = 'chipInPlay',
  userChips = 'userChips',
  remaniningTransfers = 'remainingTransfers'
}

export const getMiniLeagueColumns = ({
  isMobile,
  setSelectedMinileagueTeam
}: {
  isMobile: boolean;
  setSelectedMinileagueTeam: React.Dispatch<
    React.SetStateAction<LeagueEntry | null>
  >;
}) => [
  {
    Header: 'Team Id',
    accessor: MiniLeagueColumnAccessors.teamId
  },
  {
    Header: 'Team',
    accessor: MiniLeagueColumnAccessors.team,
    disableFilters: true,
    disableSortBy: true,
    sticky: 'left',
    Cell: ({ value }: Cell) => {
      return (
        <div>
          <BodySmallRegularBold
            className="text-primary-600 pointer"
            onClick={() => setSelectedMinileagueTeam(value.entry)}
          >
            {value.teamName}
          </BodySmallRegularBold>
          <CaptionRegular textColor="text-neutral-600">
            {value.playerName}
          </CaptionRegular>
        </div>
      );
    }
  },
  {
    Header: 'Position',
    accessor: MiniLeagueColumnAccessors.leaguePosition,
    disableFilters: true,
    style: { maxWidth: 50 },
    Cell: ({ value }: Cell) => {
      const { leagueRank, positionChange } = value;
      // A rank FPL has not published yet reads as a dash rather than a blank
      // cell, and an unknown move shows no arrow rather than a false drop.
      const hasChange = positionChange != null && positionChange !== 0;
      const isPositiveChange = (positionChange ?? 0) > 0;
      return (
        <div className="flex items-center">
          <BodySmallRegularBold
            className="mr-3"
            textColor={`w-5 h-5 flex items-center justify-center rounded-full ${
              leagueRank === 1
                ? 'bg-[#F8D849] text-neutral-600 border-[#FCEDAC] border-2'
                : leagueRank === 2
                ? 'bg-neutral-600 text-white border-[#DBDBDD] border-2'
                : leagueRank === 3
                ? 'bg-[#C19256] text-white border-[#DDC3A2] border-2'
                : ''
            }`}
          >
            {leagueRank ?? '\u2013'}
          </BodySmallRegularBold>
          <div className="flex items-center">
            {!hasChange ? null : isPositiveChange ? (
              <Icon
                name="ArrowUpRightFilled"
                className="text-success-600 w-3 h-3 mr-1"
              />
            ) : (
              <Icon
                name="ArrowUpRightFilled"
                className="text-danger-600 w-3 h-3 rotate-90 mr-1"
              />
            )}
            <BodySmallRegularBold
              textColor={
                !hasChange
                  ? ''
                  : isPositiveChange
                  ? 'text-success-600'
                  : 'text-danger-600'
              }
              className="flex font-bold mr-1"
            >
              {hasChange ? Math.abs(positionChange) : ''}
            </BodySmallRegularBold>
          </div>
        </div>
      );
    }
  },
  {
    Header: 'Live Rank',
    accessor: MiniLeagueColumnAccessors.liveRank,
    disableFilters: true,
    Cell: (cell: Cell) => (
      <div>
        <div className="py-1 px-2 inline-flex bg-primary-100 rounded-lg">
          <BodySmallRegular>
            {cell.value == null ? '\u2013' : cell.value.toLocaleString()}
          </BodySmallRegular>
        </div>
      </div>
    ),
    style: { maxWidth: 50 }
  },
  {
    Header: 'Total',
    accessor: MiniLeagueColumnAccessors.total,
    disableFilters: true,
    Cell: (cell: Cell) => (
      <div>
        <BodySmallRegular textColor="text-neutral-800">
          {cell.value}
        </BodySmallRegular>
      </div>
    )
  },
  {
    Header: isMobile ? 'Pts' : 'Points',
    accessor: MiniLeagueColumnAccessors.points,
    disableFilters: true,
    Cell: (cell: Cell) => (
      <div>
        <BodySmallRegular textColor="text-neutral-800">
          {cell.value}
        </BodySmallRegular>
      </div>
    )
  },
  {
    Header: 'Hits',
    accessor: MiniLeagueColumnAccessors.hits,
    disableFilters: true,
    Cell: (cell: Cell) => (
      <div>
        <div
          className={`py-1 px-2 inline-flex rounded-lg ${
            cell.value < 1
              ? 'bg-success-100'
              : cell.value < 5
              ? 'bg-warning-100'
              : 'bg-danger-100'
          }`}
        >
          <BodySmallRegular>{cell.value}</BodySmallRegular>
        </div>
      </div>
    )
  },
  {
    Header: 'Captain',
    accessor: MiniLeagueColumnAccessors.captain,
    disableFilters: true,
    disableSortBy: true,
    Cell: ({ value }: Cell) => {
      return (
        <div className="flex items-center">
          <div className="h-5 w-5 mr-1">
            <TeamShortNameShirt player={value} />
          </div>
          <BodySmallRegular className="mb-0">
            {value?.displayName}
          </BodySmallRegular>
        </div>
      );
    }
  },
  {
    Header: 'Vice',
    accessor: MiniLeagueColumnAccessors.viceCaptain,
    disableFilters: true,
    disableSortBy: true,
    Cell: ({ value }: Cell) => {
      return (
        <div className="flex items-center">
          <div className="h-5 w-5 mr-1">
            <TeamShortNameShirt player={value} />
          </div>
          <BodySmallRegular className="mb-0">
            {value?.displayName}
          </BodySmallRegular>
        </div>
      );
    }
  },
  {
    Header: 'Transfers',
    accessor: MiniLeagueColumnAccessors.transfers,
    disableFilters: true,
    disableSortBy: true,
    Cell: (cell: Cell) => (
      <div>
        {cell.value === null ? (
          <BodySmallRegular>{'\u2013'}</BodySmallRegular>
        ) : null}
        {cell.value?.map((transfer, index) => {
          return (
            <div
              className="flex items-center mb-px"
              key={`${transfer.playerOut.displayName}-${index}`}
            >
              <BodySmallRegular textColor="text-danger-600">
                {transfer.playerOut.displayName}
              </BodySmallRegular>
              <Icon className="mx-1" name="CompareArrows" />
              <BodySmallRegular textColor="text-success-600">
                {transfer.playerIn.displayName}
              </BodySmallRegular>
            </div>
          );
        })}
      </div>
    )
  },
  {
    Header: 'Remaining Transfers',
    accessor: MiniLeagueColumnAccessors.remaniningTransfers,
    disableFilters: true,
    disableSortBy: true,
    Cell: ({ value }: Cell) => {
      return (
        <div className="flex justify-center">
          <BodyRegular>{value ?? '\u2013'}</BodyRegular>
        </div>
      );
    }
  },
  {
    Header: 'Chip In Play',
    accessor: MiniLeagueColumnAccessors.chipInPlay,
    disableFilters: true,
    disableSortBy: true,
    Cell: ({ value }: Cell) => {
      return value?.chipName ? (
        <div className="inline-flex flex-col items-center rounded-lg bg-neutral-200 py-1 px-2">
          <BodySmallRegularBold
            data-cy="chip-in-play"
            className="uppercase"
            textColor="text-neutral-600"
          >
            {value.chipName}
          </BodySmallRegularBold>
        </div>
      ) : null;
    }
  },
  {
    Header: 'Chips Played',
    accessor: MiniLeagueColumnAccessors.userChips,
    disableFilters: true,
    disableSortBy: true,
    Cell: ({ value }: Cell) => {
      if (!value || value.length === 0) return null;
      return (
        <Tooltip
          variant="light"
          title={
            <div className="flex flex-col gap-1">
              {value.map(({ name, event }, i) => (
                <BodyExtraSmallRegular
                  key={`${name}-${i}`}
                  textColor="text-neutral-600"
                >
                  {name} - GW{event}
                </BodyExtraSmallRegular>
              ))}
            </div>
          }
        >
          <div className="inline-flex items-center gap-1 rounded-lg bg-neutral-200 py-1 px-2 cursor-pointer">
            <BodySmallRegular textColor="text-neutral-600">
              {value.length} chip{value.length !== 1 ? 's' : ''}
            </BodySmallRegular>
            <Icon className="w-3.5 h-3.5 text-neutral-500" name="InfoFilled" />
          </div>
        </Tooltip>
      );
    }
  },
  {
    Header: 'Played',
    accessor: MiniLeagueColumnAccessors.played,
    disableFilters: true,
    disableSortBy: true,
    Cell: (cell: Cell) =>
      // Null when the entry came without its squad.
      cell.value === null ? (
        <BodySmallRegular>{'\u2013'}</BodySmallRegular>
      ) : (
        <div>
          <div className="py-1 px-2 inline-flex bg-neutral-200 rounded-lg">
            <BodySmallRegular textColor="text-neutral-800">
              {cell.value}/11
            </BodySmallRegular>
          </div>
          <ProgressBar
            bgColor={theme.colors.primary[600]}
            baseBgColor="#DBDBDD"
            height="3px"
            completed={(cell.value / 11) * 100}
            isLabelVisible={false}
            className="mt-2"
            animateOnRender
          />
        </div>
      )
  }
];

export enum SeasonHistoryColumnAccessors {
  seasonName = 'seasonName',
  totalPoints = 'totalPoints',
  rank = 'rank',
  percentage = 'percentage'
}

export const seasonHistoryTableColumns = () => [
  {
    Header: 'Season',
    accessor: SeasonHistoryColumnAccessors.seasonName,
    Cell: ({ value }: Cell) =>
      value !== 'none' ? (
        <div className="inline-flex items-center py-1">
          <BodyRegular
            textColor="text-neutral-600"
            className="text-sm md:text-base"
          >
            {value.toLocaleString()}
          </BodyRegular>
        </div>
      ) : null
  },
  {
    Header: 'Total Points',
    accessor: SeasonHistoryColumnAccessors.totalPoints,
    Cell: ({ value }: Cell) =>
      value !== 'none' ? (
        <div className="inline-flex items-center py-1">
          <BodyRegular
            textColor="text-neutral-600"
            className="text-sm md:text-base"
          >
            {value.toLocaleString()}
          </BodyRegular>
        </div>
      ) : null
  },
  {
    Header: 'Rank',
    accessor: SeasonHistoryColumnAccessors.rank,
    Cell: ({ value }: Cell) => {
      return value !== 'none' ? (
        <div className="inline-flex items-center py-1">
          <BodyRegular
            textColor="text-neutral-600"
            className="text-sm md:text-base"
          >
            {value.toLocaleString()}
          </BodyRegular>
        </div>
      ) : null;
    }
  },
  {
    Header: () => (
      <div className="flex items-center">
        <BodySmallRegularBold
          className="font-bold font-open-sans text-xs"
          textColor="text-neutral-600"
        >
          Percentage
        </BodySmallRegularBold>
        <div className="hidden sm:flex">
          <Tooltip
            variant="light"
            title="This is the top % you finished at for that season."
          >
            <span className="ml-1">
              <Icon className="w-4 h-4" name="InfoFilled" />
            </span>
          </Tooltip>
        </div>
      </div>
    ),
    accessor: SeasonHistoryColumnAccessors.percentage,
    Cell: ({ value }: Cell) =>
      value !== 'none' ? (
        <div className="inline-flex items-center py-1">
          <BodyRegular
            textColor={
              value < 1
                ? 'text-[#DDA42E]'
                : value < 10
                ? 'text-success-600'
                : value < 25
                ? 'text-neutral-600'
                : 'text-danger-600'
            }
            className="ml-1 p-1 rounded text-sm md:text-base"
          >
            {getSeasonPercentageEmojiMedals(value)}
          </BodyRegular>
        </div>
      ) : null
  }
];

const getSeasonPercentageEmojiMedals = (percentage) => {
  if (percentage < 0.5) {
    return `🏆 ${percentage.toFixed(2)}%`;
  }
  if (percentage < 1) {
    return `🥇 ${percentage.toFixed(2)}%`;
  }
  if (percentage < 10) {
    return `🥈 ${percentage.toFixed(2)}%`;
  }
  if (percentage < 25) {
    return `🥉 ${percentage.toFixed(2)}%`;
  }
  return `${percentage.toFixed(2)}%`;
};
